import React, { useEffect, useState } from "react";
import { Button, Card, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { getConfig, serverUrl } from "../../serverUrl";

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `KSh ${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getApiErrorMessage = (error) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  "Failed to update opening balance.";

export default function OccupancyOpeningBalanceCard({ house, selectedOccupancy }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localBalance, setLocalBalance] = useState({
    recordId: null,
    amount: "",
    current: 0,
  });

  useEffect(() => {
    const occupancyAmount = selectedOccupancy?.occupancy_opening_balance_amount;
    const currentBalance = selectedOccupancy?.occupancy_id
      ? Number(selectedOccupancy?.effective_opening_balance || 0)
      : Number(house?.opening_balance_amount || 0);

    setLocalBalance({
      recordId: selectedOccupancy?.occupancy_opening_balance_id || null,
      amount:
        occupancyAmount !== undefined && occupancyAmount !== null
          ? String(occupancyAmount)
          : String(currentBalance),
      current: currentBalance,
    });
    setIsEditing(false);
  }, [house?.opening_balance_amount, selectedOccupancy]);

  const handleSave = async () => {
    if (!selectedOccupancy?.occupancy_id) {
      toast.error("Select an occupancy before editing the opening balance.");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        occupancy: selectedOccupancy.occupancy_id,
        amount: Number(localBalance.amount || 0),
        source: "manual",
      };

      const config = getConfig();
      let response;
      if (localBalance.recordId) {
        response = await serverUrl.patch(
          `/landlord/occupancy-opening-balance/${localBalance.recordId}/`,
          payload,
          config
        );
      } else {
        response = await serverUrl.post(
          "/landlord/occupancy-opening-balance/",
          payload,
          config
        );
      }

      const updatedAmount = Number(response.data?.amount || 0);
      setLocalBalance((prev) => ({
        ...prev,
        recordId: response.data?.id || prev.recordId,
        amount: String(updatedAmount),
        current: updatedAmount,
      }));
      setIsEditing(false);
      toast.success("Opening balance updated.");
    } catch (error) {
      console.error(error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const hasSelectedOccupancy = Boolean(selectedOccupancy?.occupancy_id);

  return (
    <Card className="mb-3 border-0 shadow-sm">
      <Card.Body>
        <div className="flex justify-between items-start gap-3 mb-3">
          <div>
            <div className="font-semibold text-gray-900 text-lg">
              Opening Balance
            </div>
            <div className="text-gray-500 text-sm">
              {hasSelectedOccupancy
                ? "This balance applies to the selected tenant."
                : "This is the opening balance currently set for this unit."}
            </div>
          </div>
          {hasSelectedOccupancy && !isEditing && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              Adjust
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3">
          <div className="bg-gray-50 p-3 rounded">
            <div className="text-gray-500 text-xs uppercase">Current Balance</div>
            <div className="font-semibold text-lg">
              {formatCurrency(localBalance.current)}
            </div>
          </div>
        </div>

        {isEditing && hasSelectedOccupancy && (
          <div className="mt-4 pt-3 border-top">
            <Form.Group className="mb-3">
              <Form.Label>Opening Balance Amount</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={localBalance.amount}
                onChange={(event) =>
                  setLocalBalance((prev) => ({
                    ...prev,
                    amount: event.target.value,
                  }))
                }
              />
            </Form.Group>

            <div className="flex gap-2">
              <Button
                variant="primary"
                disabled={isSaving}
                onClick={handleSave}
              >
                {isSaving ? "Saving..." : "Save Balance"}
              </Button>
              <Button
                variant="outline-secondary"
                disabled={isSaving}
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
